// src/components/resumo/quiz/QuizBadgeManager.ts
// 🎯 Manager para controlar o cartão de Quiz no editor
// Mesmo visual do cartão usado na leitura (QuizCard.css) — aqui o clique
// sempre abre o modal de autoria (QuizAdminModal), nunca expande inline.

import { QuizBadgeData } from './quiz.types';

const ICON_HELP = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m3 17 2 2 4-4"/><path d="m3 7 2 2 4-4"/><path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/></svg>';
const ICON_CHEVRON = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

export class QuizBadgeManager {
    private editorRef: React.RefObject<HTMLDivElement | null>;
    private quizDataStore: Map<string, QuizBadgeData>;
    private onOpenAdminModal: ((quizId: string, data: QuizBadgeData | null) => void) | null;
    private onQuizChange: (() => void) | null;
    private globalClickHandler: ((e: Event) => void) | null = null;

    constructor(
        editorRef: React.RefObject<HTMLDivElement | null>,
        onOpenAdminModal?: (quizId: string, data: QuizBadgeData | null) => void,
        _onOpenStudentModal?: (quizId: string, data: QuizBadgeData) => void,
        onQuizChange?: () => void
    ) {
        this.editorRef = editorRef;
        this.quizDataStore = new Map();
        this.onOpenAdminModal = onOpenAdminModal || null;
        this.onQuizChange = onQuizChange || null;

        // ✅ Event delegation - um único listener para todos os cartões
        this.setupGlobalListener();
    }

    private setupGlobalListener() {
        this.globalClickHandler = (e: Event) => {
            const target = e.target as HTMLElement;
            const card = target.closest('[data-quiz-id]') as HTMLElement;
            if (card) {
                this.handleCardClick(e, card);
            }
        };

        document.addEventListener('click', this.globalClickHandler, true);
    }

    // Sem `color`, o cartão usa o degradê/textura premium padrão definido em
    // QuizCard.css. Só sobrescreve com inline style quando o admin escolhe
    // explicitamente uma cor no seletor de cores da toolbar.
    private buildCardHTML(quizId: string, questionCount: number, color?: string): string {
        const dataColor = color ? ` data-badge-color="${color}"` : '';
        const borderStyle = color ? ` style="border-left-color: ${color};"` : '';
        const iconStyle = color ? ` style="background: ${color};"` : '';
        const titleStyle = color ? ` style="color: ${color};"` : '';
        const chevronStyle = color ? ` style="color: ${color};"` : '';
        return `<div class="quiz-card quiz-card-edit" data-quiz-id="${quizId}"${dataColor} contenteditable="false"${borderStyle}><div class="quiz-card-trigger"><span class="quiz-card-icon"${iconStyle}>${ICON_HELP}</span><span class="quiz-card-text"><span class="quiz-card-title"${titleStyle}>Quiz</span><span class="quiz-card-count">${questionCount} pergunta${questionCount !== 1 ? 's' : ''} · clique para configurar</span></span><span class="quiz-card-chevron"${chevronStyle}>${ICON_CHEVRON}</span></div></div>`;
    }

    /**
     * Insere um novo cartão de Quiz na posição do cursor
     */
    insertQuizBadge(badgeColor?: string): string | null {
        const editor = this.editorRef.current;
        if (!editor) return null;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0);

        if (!editor.contains(range.commonAncestorContainer)) {
            console.warn('Cursor fora do editor');
            return null;
        }

        const quizId = `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.buildCardHTML(quizId, 0, badgeColor) + '&nbsp;';
        const badgeNode = tempDiv.firstChild;
        const spacerNode = tempDiv.lastChild;

        if (badgeNode) {
            range.deleteContents();
            range.insertNode(badgeNode);
            if (spacerNode && spacerNode !== badgeNode) {
                badgeNode.parentNode?.insertBefore(spacerNode, badgeNode.nextSibling);
            }

            range.setStartAfter(badgeNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);

            // Salvar quiz vazio no store
            this.quizDataStore.set(quizId, {
                id: quizId,
                questions: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            console.log('✅ Quiz card inserido:', quizId);
            return quizId;
        }

        return null;
    }

    /**
     * Inicializa/normaliza todos os cartões existentes no editor — atualiza
     * classes, contenteditable e o texto de contagem de perguntas. Cartões em
     * formato legado (badge inline antigo) são reconstruídos no novo formato.
     */
    initializeAllBadges(isEditMode: boolean = true) {
        const editor = this.editorRef.current;
        if (!editor) return;

        const cards = editor.querySelectorAll('[data-quiz-id]');

        cards.forEach((card) => {
            const cardEl = card as HTMLElement;
            const quizId = cardEl.dataset.quizId;
            if (!quizId) return;

            const questionCount = this.quizDataStore.get(quizId)?.questions.length ?? 0;
            const isLegacyFormat = !cardEl.querySelector(':scope > .quiz-card-trigger');

            if (isLegacyFormat) {
                cardEl.outerHTML = this.buildCardHTML(quizId, questionCount, cardEl.dataset.badgeColor || undefined);
                return;
            }

            cardEl.classList.remove('quiz-badge', 'quiz-badge-edit', 'quiz-badge-read', 'quiz-card-read');
            cardEl.classList.add('quiz-card', isEditMode ? 'quiz-card-edit' : 'quiz-card-read');
            cardEl.setAttribute('contenteditable', 'false');

            const countEl = cardEl.querySelector('.quiz-card-count');
            if (countEl) {
                countEl.textContent = `${questionCount} pergunta${questionCount !== 1 ? 's' : ''} · clique para configurar`;
            }
        });

        console.log(`✅ ${cards.length} quiz cards inicializados (${isEditMode ? 'edit' : 'read'} mode)`);
    }

    private handleCardClick(e: Event, card: HTMLElement) {
        e.preventDefault();
        e.stopPropagation();

        const quizId = card.dataset.quizId;
        if (!quizId) return;

        const quizData = this.quizDataStore.get(quizId);
        console.log('🖱️ Quiz clicado (editor):', { quizId, hasData: !!quizData });

        this.onOpenAdminModal?.(quizId, quizData || null);
    }

    saveQuizData(quizId: string, data: QuizBadgeData) {
        this.quizDataStore.set(quizId, data);
        this.onQuizChange?.();
        console.log('✅ Quiz salvo:', quizId, data.questions.length, 'perguntas');

        // Atualiza a contagem exibida no cartão sem precisar re-varrer o editor inteiro
        const editor = this.editorRef.current;
        const countEl = editor?.querySelector(`[data-quiz-id="${quizId}"] .quiz-card-count`);
        if (countEl) {
            countEl.textContent = `${data.questions.length} pergunta${data.questions.length !== 1 ? 's' : ''} · clique para configurar`;
        }
    }

    getQuizData(quizId: string): QuizBadgeData | null {
        return this.quizDataStore.get(quizId) || null;
    }

    exportAllQuizzes(): Record<string, QuizBadgeData> {
        const exported: Record<string, QuizBadgeData> = {};

        console.log('📤 Exportando quizzes:', {
            totalInStore: this.quizDataStore.size,
            quizIds: Array.from(this.quizDataStore.keys())
        });

        this.quizDataStore.forEach((data, id) => {
            exported[id] = data;
            console.log(`  ✅ Quiz ${id}:`, data.questions.length, 'perguntas');
        });

        return exported;
    }

    importQuizzes(quizzes: Record<string, QuizBadgeData>) {
        Object.entries(quizzes).forEach(([id, data]) => {
            this.quizDataStore.set(id, data);
        });
        console.log('✅ Quizzes importados:', Object.keys(quizzes).length);
    }

    removeQuiz(quizId: string) {
        this.quizDataStore.delete(quizId);
        this.onQuizChange?.();

        const editor = this.editorRef.current;
        if (editor) {
            const card = editor.querySelector(`[data-quiz-id="${quizId}"]`);
            if (card) {
                card.remove();
            }
        }
    }

    /**
     * ✅ Cleanup - remove listener global
     */
    cleanup() {
        if (this.globalClickHandler) {
            document.removeEventListener('click', this.globalClickHandler, true);
            this.globalClickHandler = null;
        }
    }
}
